import * as React from 'react';

const COLLAPSED_KEY = 'wer-admin-sidebar-collapsed';

/** localStorage that survives private mode / storage-denied embeds. */
export const safeStorage = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* preference simply does not persist */
    }
  },
};

interface SidebarContextValue {
  /** Desktop-only preference; the mobile drawer always shows full labels. */
  collapsed: boolean;
  toggle: () => void;
  setCollapsed: (next: boolean) => void;
}

const SidebarContext = React.createContext<SidebarContextValue | null>(null);

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  );
}

/**
 * Owns the collapsed/expanded preference so the header, the sidebar, and the
 * peel under-layer all read one source of truth. Cmd/Ctrl+B toggles it, same
 * as every editor the console's users already live in.
 */
export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsedState] = React.useState(
    () => safeStorage.get(COLLAPSED_KEY) === '1',
  );

  const setCollapsed = React.useCallback((next: boolean) => {
    setCollapsedState(next);
    safeStorage.set(COLLAPSED_KEY, next ? '1' : '0');
  }, []);

  const toggle = React.useCallback(() => {
    setCollapsedState((previous) => {
      const next = !previous;
      safeStorage.set(COLLAPSED_KEY, next ? '1' : '0');
      return next;
    });
  }, []);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'b' || !(event.metaKey || event.ctrlKey)) return;
      if (event.altKey || event.shiftKey || isTypingTarget(event.target)) return;
      event.preventDefault();
      toggle();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggle]);

  const value = React.useMemo(
    () => ({ collapsed, toggle, setCollapsed }),
    [collapsed, toggle, setCollapsed],
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar(): SidebarContextValue {
  const context = React.useContext(SidebarContext);
  if (!context) throw new Error('useSidebar must be used within SidebarProvider');
  return context;
}
